import type { FiltersBarType } from '@deepdive-ui';
import { filtersBarFilters } from '@/components/UI/organisms/FiltersBar/filters.fixtures';

/**
 * The bridge between a row's fields and the filter chips.
 *
 * A row field is singular (`channel`) and the chip that filters it is plural
 * (`channels`), because the chips are named for what they contain and the field
 * for what one row holds. Both names are load-bearing and neither can be derived
 * from the other, so the mapping is written down once, here.
 *
 * Every value list is **read out of the chip fixtures** rather than retyped.
 * `FilterChipOption.value` is a `string`, never a literal union, so TypeScript
 * cannot enforce that the corpus and the chips agree — deriving the lists is the
 * next best thing, and `assertRowsMatchFixtures` closes the remaining gap at
 * runtime.
 *
 * This is the one place `src/app/data` reaches into a `.tsx` fixture, which
 * carries the chips' JSX icons with it. The alternative — a second hand-written
 * copy of every option list — is the drift this module exists to prevent, so the
 * import is the cheaper of the two.
 */

export type DimensionKey = 'brand' | 'channel' | 'intent' | 'topic' | 'conversation' | 'sentiment';

export const dimensionKeys = [
  'brand',
  'channel',
  'intent',
  'topic',
  'conversation',
  'sentiment',
] as const satisfies readonly DimensionKey[];

/** Row field → the id of the chip that filters it. */
export const DIMENSION_FILTER_ID: Readonly<Record<DimensionKey, string>> = {
  brand: 'brands',
  channel: 'channels',
  intent: 'intents',
  topic: 'topics',
  conversation: 'conversations',
  sentiment: 'sentiments',
};

/** Chip id → row field. The inverse of `DIMENSION_FILTER_ID`, derived from it. */
export const FILTER_ID_DIMENSION: Readonly<Record<string, DimensionKey>> = Object.fromEntries(
  dimensionKeys.map((key) => [DIMENSION_FILTER_ID[key], key]),
) as Record<string, DimensionKey>;

const optionsFor = (filterId: string) => {
  const filter = filtersBarFilters.find((candidate) => candidate.id === filterId);
  if (!filter) {
    throw new Error(
      `dimensions: no filter chip with id "${filterId}". The chip fixtures and DIMENSION_FILTER_ID have diverged.`,
    );
  }
  return filter.options;
};

const build = <T,>(make: (key: DimensionKey) => T): Readonly<Record<DimensionKey, T>> =>
  Object.fromEntries(dimensionKeys.map((key) => [key, make(key)])) as Record<DimensionKey, T>;

/**
 * Every value a dimension can take, **in the chips' own order**.
 *
 * The order is not cosmetic. Series colours are indexed off this array, so it is
 * what keeps Grameenphone on `chart-1` whether or not it survives the current
 * filter — see `dimensionIndex`.
 */
export const dimensionValues: Readonly<Record<DimensionKey, readonly string[]>> = build((key) =>
  optionsFor(DIMENSION_FILTER_ID[key]).map((option) => option.value),
);

/** `network-coverage` → `Network coverage`, for axis ticks and legends. */
export const dimensionLabels: Readonly<Record<DimensionKey, Readonly<Record<string, string>>>> =
  build((key) =>
    Object.fromEntries(optionsFor(DIMENSION_FILTER_ID[key]).map((o) => [o.value, o.label])),
  );

/**
 * Value → its position in the chip's option list.
 *
 * This is what series colours index into, and the reason is a correctness bug
 * that reads as a rendering glitch: colour by position in the *filtered result*
 * and deselecting Grameenphone hands `chart-1` to Banglalink, recolouring the
 * whole page mid-session. Indexing the fixture order pins every value to one
 * colour for the life of the session.
 */
export const dimensionIndex: Readonly<Record<DimensionKey, Readonly<Record<string, number>>>> =
  build((key) =>
    Object.fromEntries(optionsFor(DIMENSION_FILTER_ID[key]).map((o, i) => [o.value, i])),
  );

export function labelOf(key: DimensionKey, value: string): string {
  return dimensionLabels[key][value] ?? value;
}

/** A set per dimension, for the load-time check and for O(1) membership tests. */
const valueSets = build((key) => new Set(dimensionValues[key]));

/**
 * Throws if any row carries a value no chip offers.
 *
 * Runs once at module load, over the whole corpus. Without it a renamed or
 * deleted chip option turns into an empty chart — the filter quietly matches
 * nothing and the page draws a flat zero line, which looks like a data problem
 * rather than the fixture edit it is. Failing loudly at boot is worth the ~1ms.
 */
export function assertRowsMatchFixtures(
  rows: readonly Readonly<Record<DimensionKey, string>>[],
): void {
  for (const key of dimensionKeys) {
    const allowed = valueSets[key];
    for (const row of rows) {
      if (!allowed.has(row[key])) {
        throw new Error(
          `dimensions: row has ${key}="${row[key]}", which the "${DIMENSION_FILTER_ID[key]}" chip does not offer. ` +
            `Allowed: ${[...allowed].join(', ')}.`,
        );
      }
    }
  }
}

/**
 * The dimensions a module actually filters by — those whose chip it shows.
 *
 * **A module must never filter by a chip it does not render.** Brand Health has
 * no Brands chip (the fixture declares `types: ['competitors']`), so applying
 * the brand selection there would silently drop rows against a control the user
 * cannot see, reach, or clear. It measurably did: the default selection carries
 * three brands, so the module was cutting its own corpus by nearly half to
 * honour a filter that was not on screen.
 *
 * The rule is `FiltersBar`'s own — `!filter.types || filter.types.includes(type)`
 * — read from the same fixtures the bar reads, so the chips on screen and the
 * dimensions in the query cannot drift apart.
 *
 * Scoping to our own brand is a separate matter and still applies: see
 * `data/brand.ts`. That is what the module *is*, not a filter over it.
 */
export function dimensionsForFiltersType(type: FiltersBarType): DimensionKey[] {
  return dimensionKeys.filter((key) => {
    const filter = filtersBarFilters.find((f) => f.id === DIMENSION_FILTER_ID[key]);
    return filter !== undefined && (!filter.types || filter.types.includes(type));
  });
}
